import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
  CommandShortcut,
} from "./ui/command";
import {
  LayoutDashboard, BookOpen, AlertTriangle, FileText, Bug, Shield,
  Users, FileCheck, Link2, ClipboardCheck, Paperclip, Wrench, CalendarDays,
  BarChart3, ShieldAlert, Mail, Target, Sparkles, Award, Building2,
  GraduationCap, TrendingUp, Brain, Cloud, Globe, Upload, CreditCard,
  PenLine, Crown, Settings, LogOut, Moon, Sun, Plus, FileDown, Search,
} from "lucide-react";
import { useAuth, useTheme } from "../App";
import { useI18n } from "../i18n/I18nProvider";

const RECENT_KEY = "complianceos_cmd_recent";
const RECENT_LIMIT = 5;

const NAV_ITEMS = [
  // Overview
  { id: "dashboard", icon: LayoutDashboard, label: "Dashboard", labelEs: "Tablero", path: "/dashboard", group: "navigate", shortcut: "G D" },
  { id: "calendar", icon: CalendarDays, label: "Compliance Calendar", labelEs: "Calendario", path: "/calendar", group: "navigate" },
  // Compliance
  { id: "controls", icon: BookOpen, label: "Control Library", labelEs: "Biblioteca de Controles", path: "/controls", group: "navigate", shortcut: "G C" },
  { id: "soa", icon: FileCheck, label: "Statement of Applicability", labelEs: "Declaración de Aplicabilidad", path: "/soa", group: "navigate" },
  { id: "mappings", icon: Link2, label: "Framework Mappings", labelEs: "Mapeos entre Marcos", path: "/mappings", group: "navigate" },
  { id: "gap", icon: Target, label: "Gap Analysis", labelEs: "Análisis de Brechas", path: "/gap-analysis", group: "navigate", shortcut: "G G" },
  { id: "cert", icon: Award, label: "Certification Readiness", labelEs: "Listo para Certificación", path: "/certification", group: "navigate" },
  // Risk
  { id: "risks", icon: AlertTriangle, label: "Risk Register", labelEs: "Registro de Riesgos", path: "/risks", group: "navigate", shortcut: "G R" },
  { id: "heatmap", icon: BarChart3, label: "Risk Heat Map", labelEs: "Mapa de Calor", path: "/risk-heatmap", group: "navigate" },
  { id: "threats", icon: ShieldAlert, label: "Threat Intelligence", labelEs: "Inteligencia de Amenazas", path: "/threat-intel", group: "navigate" },
  { id: "vuln", icon: Shield, label: "Vulnerability Scanner", labelEs: "Escáner de Vulnerabilidades", path: "/vuln-scanner", group: "navigate" },
  { id: "predictor", icon: Brain, label: "AI Risk Intelligence", labelEs: "Inteligencia de Riesgos AI", path: "/risk-predictor", group: "navigate" },
  { id: "incidents", icon: Bug, label: "Incidents", labelEs: "Incidentes", path: "/incidents", group: "navigate", shortcut: "G I" },
  // Governance
  { id: "documents", icon: FileText, label: "Documents", labelEs: "Documentos", path: "/documents", group: "navigate" },
  { id: "evidence", icon: Paperclip, label: "Evidence", labelEs: "Evidencias", path: "/evidence", group: "navigate" },
  { id: "audits", icon: ClipboardCheck, label: "Internal Audits", labelEs: "Auditorías Internas", path: "/audits", group: "navigate", shortcut: "G A" },
  { id: "mgmt", icon: Users, label: "Management Review", labelEs: "Revisión por la Dirección", path: "/management-review", group: "navigate" },
  { id: "ca", icon: Wrench, label: "Corrective Actions", labelEs: "Acciones Correctivas", path: "/corrective-actions", group: "navigate" },
  // Org
  { id: "vendors", icon: Building2, label: "Vendor Risk", labelEs: "Riesgo de Proveedores", path: "/vendor-risk", group: "navigate" },
  { id: "training", icon: GraduationCap, label: "Training & Awareness", labelEs: "Capacitación", path: "/training", group: "navigate" },
  { id: "maintenance", icon: CalendarDays, label: "Maintenance", labelEs: "Mantenimiento", path: "/maintenance", group: "navigate" },
  // Reports
  { id: "reports", icon: BarChart3, label: "Reports & Export", labelEs: "Reportes y Exportar", path: "/reports", group: "navigate" },
  { id: "exec", icon: FileText, label: "Executive Report", labelEs: "Reporte Ejecutivo", path: "/exec-report", group: "navigate" },
  { id: "bench", icon: TrendingUp, label: "Benchmarking", labelEs: "Benchmarking", path: "/benchmarking", group: "navigate" },
  // AI
  { id: "policy", icon: Sparkles, label: "AI Policy Generator", labelEs: "Generador de Políticas AI", path: "/policy-generator", group: "navigate" },
  { id: "wizard", icon: Target, label: "Implementation Wizard", labelEs: "Asistente de Implementación", path: "/wizard", group: "navigate", shortcut: "⌘ W" },
  { id: "evcoll", icon: Cloud, label: "Auto Evidence Collection", labelEs: "Recolección Automática", path: "/evidence-collection", group: "navigate" },
  // Settings
  { id: "email", icon: Mail, label: "Email Settings", labelEs: "Configuración de Email", path: "/email-settings", group: "settings" },
  { id: "esign", icon: PenLine, label: "Electronic Signature", labelEs: "Firma Electrónica", path: "/esignature-settings", group: "settings" },
  { id: "rbac", icon: Users, label: "Access Control (RBAC)", labelEs: "Control de Acceso", path: "/rbac", group: "settings" },
  { id: "tenants", icon: Building2, label: "Multi-Tenancy", labelEs: "Multi-Tenant", path: "/tenants", group: "settings" },
  { id: "billing", icon: CreditCard, label: "Billing", labelEs: "Facturación", path: "/billing", group: "settings" },
  { id: "portal", icon: Link2, label: "Client Portal", labelEs: "Portal de Cliente", path: "/portal", group: "settings" },
  { id: "bulk", icon: Upload, label: "Bulk Import/Export", labelEs: "Importación Masiva", path: "/bulk", group: "settings" },
];

export function CommandPalette({ open, onOpenChange }) {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { locale, setLocale } = useI18n();
  const isES = locale === "es";

  const [recent, setRecent] = useState(() => {
    try {
      const raw = localStorage.getItem(RECENT_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch { return []; }
  });

  const close = useCallback(() => onOpenChange(false), [onOpenChange]);

  const pushRecent = useCallback((id) => {
    setRecent((prev) => {
      const next = [id, ...prev.filter((x) => x !== id)].slice(0, RECENT_LIMIT);
      try { localStorage.setItem(RECENT_KEY, JSON.stringify(next)); } catch {}
      return next;
    });
  }, []);

  const go = useCallback((item) => {
    pushRecent(item.id);
    close();
    navigate(item.path);
  }, [navigate, pushRecent, close]);

  const recentItems = useMemo(
    () => recent
      .map((id) => NAV_ITEMS.find((it) => it.id === id))
      .filter(Boolean),
    [recent],
  );

  const navByGroup = useMemo(() => {
    const groups = { navigate: [], settings: [] };
    NAV_ITEMS.forEach((it) => {
      if (groups[it.group]) groups[it.group].push(it);
    });
    return groups;
  }, []);

  const renderLabel = (item) => isES ? item.labelEs : item.label;

  return (
    <CommandDialog open={open} onOpenChange={onOpenChange}>
      <CommandInput
        placeholder={isES ? "Buscar páginas, acciones..." : "Search pages, actions..."}
        aria-label={isES ? "Buscar" : "Search"}
      />
      <CommandList>
        <CommandEmpty>
          {isES ? "Sin resultados." : "No results found."}
        </CommandEmpty>

        {recentItems.length > 0 && (
          <>
            <CommandGroup heading={isES ? "Recientes" : "Recent"}>
              {recentItems.map((item) => {
                const Icon = item.icon;
                return (
                  <CommandItem
                    key={`recent-${item.id}`}
                    value={`recent ${renderLabel(item)}`}
                    onSelect={() => go(item)}
                  >
                    <Icon />
                    <span>{renderLabel(item)}</span>
                  </CommandItem>
                );
              })}
            </CommandGroup>
            <CommandSeparator />
          </>
        )}

        <CommandGroup heading={isES ? "Navegar" : "Navigate"}>
          {navByGroup.navigate.map((item) => {
            const Icon = item.icon;
            return (
              <CommandItem
                key={item.id}
                value={`nav ${renderLabel(item)}`}
                onSelect={() => go(item)}
              >
                <Icon />
                <span>{renderLabel(item)}</span>
                {item.shortcut && <CommandShortcut>{item.shortcut}</CommandShortcut>}
              </CommandItem>
            );
          })}
        </CommandGroup>

        <CommandSeparator />

        <CommandGroup heading={isES ? "Acciones rápidas" : "Quick actions"}>
          <CommandItem
            value="action new risk"
            onSelect={() => { close(); navigate("/risks?new=1"); }}
          >
            <Plus />
            <span>{isES ? "Nuevo riesgo" : "New risk"}</span>
            <CommandShortcut>N R</CommandShortcut>
          </CommandItem>
          <CommandItem
            value="action new incident"
            onSelect={() => { close(); navigate("/incidents?new=1"); }}
          >
            <Plus />
            <span>{isES ? "Nuevo incidente" : "New incident"}</span>
            <CommandShortcut>N I</CommandShortcut>
          </CommandItem>
          <CommandItem
            value="action export executive"
            onSelect={() => { close(); navigate("/dashboard?export=executive"); }}
          >
            <FileDown />
            <span>{isES ? "Exportar reporte ejecutivo" : "Export executive report"}</span>
          </CommandItem>
        </CommandGroup>

        <CommandSeparator />

        <CommandGroup heading={isES ? "Configuración" : "Settings"}>
          {navByGroup.settings.map((item) => {
            const Icon = item.icon;
            return (
              <CommandItem
                key={item.id}
                value={`settings ${renderLabel(item)}`}
                onSelect={() => go(item)}
              >
                <Icon />
                <span>{renderLabel(item)}</span>
              </CommandItem>
            );
          })}
          {user?.is_super_admin && (
            <CommandItem
              value="settings platform admin"
              onSelect={() => { close(); navigate("/platform-admin"); }}
            >
              <Crown />
              <span>{isES ? "Admin de Plataforma" : "Platform Admin"}</span>
            </CommandItem>
          )}
        </CommandGroup>

        <CommandSeparator />

        <CommandGroup heading={isES ? "Preferencias" : "Preferences"}>
          <CommandItem
            value="pref toggle theme"
            onSelect={() => { toggleTheme(); close(); }}
          >
            {theme === "dark" ? <Sun /> : <Moon />}
            <span>
              {theme === "dark"
                ? (isES ? "Cambiar a tema claro" : "Switch to light theme")
                : (isES ? "Cambiar a tema oscuro" : "Switch to dark theme")}
            </span>
            <CommandShortcut>⌘ ⇧ L</CommandShortcut>
          </CommandItem>
          <CommandItem
            value="pref language"
            onSelect={() => { setLocale(isES ? "en" : "es"); close(); }}
          >
            <Globe />
            <span>{isES ? "Cambiar a inglés" : "Switch to Spanish"}</span>
          </CommandItem>
          <CommandItem
            value="pref logout"
            onSelect={() => { logout(); close(); navigate("/"); }}
          >
            <LogOut />
            <span>{isES ? "Cerrar sesión" : "Log out"}</span>
          </CommandItem>
        </CommandGroup>
      </CommandList>
    </CommandDialog>
  );
}

export function useCommandPalette() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onKeyDown = (e) => {
      // ⌘K / Ctrl+K
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((v) => !v);
        return;
      }
      // Quick "G + X" navigations (vim-style), ignore when in inputs
      const tag = (e.target?.tagName || "").toLowerCase();
      if (["input", "textarea", "select"].includes(tag) || e.target?.isContentEditable) return;
      // (kept simple — full chord support is out of scope for sprint 1)
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  return { open, setOpen };
}
